import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  FormatBold,
  FormatClear,
  FormatItalic,
  FormatPaint,
  FormatUnderlined,
  FormatListBulleted,
} from '@mui/icons-material'
import { sanitizaHtml } from '../FormattedHtml/FormattedHtml'

const escapeHtml = (value) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')

const paraHtml = (value = '') => {
  if (/<\/?[a-z][\s\S]*>/i.test(value)) return value

  return escapeHtml(value).replace(/\n/g, '<br>')
}

const cores = ['#1f2937', '#b91c1c', '#1d4ed8', '#047857', '#7e22ce']

const normalizaHtml = (html) => sanitizaHtml(html)

// Alguns aplicativos disponibilizam a formatação no clipboard como texto
// puro, antes do conteúdo visível (ex.: "p.p1 { ... } span.s1 { ... }").
// Esse cabeçalho não deve aparecer na receita.
const removeFolhaDeEstilosColada = (valor = '') =>
  String(valor).replace(
    /^\s*(?:(?:[a-z][\w-]*(?:[.#][\w-]+)*\s*)\{[^{}]*\}\s*)+/i,
    '',
  )

const normalizaTextoColado = (valor = '') => {
  const textoSemEstilos = removeFolhaDeEstilosColada(valor)
  const area = document.createElement('textarea')
  area.innerHTML = textoSemEstilos
  return area.value
}

const textoSemFormatacaoParaHtml = (texto) =>
  `<span style="color: #1f2937; font-weight: normal; font-style: normal; text-decoration: none;">${escapeHtml(
    normalizaTextoColado(texto),
  ).replace(/\r?\n/g, '<br>')}</span>`

/**
 * Editor de texto rico pequeno e autocontido para campos administrativos.
 * Mantém o valor em HTML para que a mesma informação possa ser renderizada
 * posteriormente no documento, quando o fluxo de receita for preparado.
 */
const FormattedTextEditor = ({
  value,
  onChange,
  label,
  disabled = false,
  error = false,
  helperText,
  minHeight = 160,
  normalizarConteudo = normalizaHtml,
  editorSx,
}) => {
  const editorRef = useRef(null)
  const rangeRef = useRef(null)
  const valorPublicadoRef = useRef(null)
  const [conteudoColado, setConteudoColado] = useState(null)

  useEffect(() => {
    const editor = editorRef.current
    const proximoValor = normalizarConteudo(
      normalizaHtml(paraHtml(value || '')),
    )

    if (editor && document.activeElement === editor) return

    if (proximoValor === valorPublicadoRef.current) return

    if (editor && editor.innerHTML !== proximoValor) {
      editor.innerHTML = proximoValor
    }
    valorPublicadoRef.current = proximoValor
  }, [value, normalizarConteudo])

  const guardarSelecao = () => {
    const selecao = window.getSelection()
    if (
      selecao?.rangeCount &&
      editorRef.current?.contains(selecao.anchorNode)
    ) {
      rangeRef.current = selecao.getRangeAt(0).cloneRange()
    }
  }

  const restaurarSelecao = () => {
    const selecao = window.getSelection()
    if (!rangeRef.current || !selecao) return

    selecao.removeAllRanges()
    selecao.addRange(rangeRef.current)
  }

  const publicar = () => {
    const html = normalizarConteudo(
      normalizaHtml(editorRef.current?.innerHTML || ''),
    )
    valorPublicadoRef.current = html
    onChange?.(html === '<br>' ? '' : html)
    guardarSelecao()
  }

  const executar = (comando, valor) => {
    if (disabled) return

    editorRef.current?.focus()
    restaurarSelecao()
    document.execCommand(comando, false, valor)
    publicar()
  }

  const colar = (event) => {
    if (disabled) return

    event.preventDefault()
    guardarSelecao()
    setConteudoColado({
      html: event.clipboardData?.getData('text/html') || '',
      texto: event.clipboardData?.getData('text/plain') || '',
    })
  }

  const aplicarColagem = (manterFormatacao) => {
    if (!conteudoColado) return

    const conteudo = normalizarConteudo(
      manterFormatacao && conteudoColado.html
        ? normalizaHtml(removeFolhaDeEstilosColada(conteudoColado.html))
        : textoSemFormatacaoParaHtml(conteudoColado.texto),
    )

    editorRef.current?.focus()
    restaurarSelecao()
    document.execCommand('insertHTML', false, conteudo)
    setConteudoColado(null)
    publicar()
  }

  return (
    <Box>
      {label && (
        <Typography
          component="label"
          variant="body2"
          sx={{
            display: 'block',
            mb: 0.75,
            color: error ? 'error.main' : 'text.secondary',
          }}
        >
          {label}
        </Typography>
      )}
      <Paper
        variant="outlined"
        sx={{
          overflow: editorSx ? 'auto' : 'hidden',
          borderColor: error ? 'error.main' : 'divider',
          '&:focus-within': { borderColor: 'primary.main', borderWidth: 2 },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.25}
          flexWrap="wrap"
          sx={{
            px: 0.75,
            py: 0.5,
            bgcolor: 'grey.50',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Tooltip title="Negrito">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => executar('bold')}
              >
                <FormatBold fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Itálico">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => executar('italic')}
              >
                <FormatItalic fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Sublinhado">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => executar('underline')}
              >
                <FormatUnderlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Lista">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => executar('insertUnorderedList')}
              >
                <FormatListBulleted fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remover formatação">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => executar('removeFormat')}
              >
                <FormatClear fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Stack
            direction="row"
            spacing={0.35}
            sx={{ ml: 0.75, alignItems: 'center' }}
          >
            {cores.map((cor) => (
              <Tooltip title={`Aplicar cor ${cor}`} key={cor}>
                <Box
                  component="button"
                  type="button"
                  disabled={disabled}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => executar('foreColor', cor)}
                  aria-label={`Aplicar cor ${cor}`}
                  sx={{
                    width: 18,
                    height: 18,
                    p: 0,
                    borderRadius: '50%',
                    border: '1px solid',
                    borderColor: 'grey.400',
                    bgcolor: cor,
                    cursor: disabled ? 'default' : 'pointer',
                  }}
                />
              </Tooltip>
            ))}
          </Stack>
        </Stack>

        <Box
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={label}
          onInput={publicar}
          onPaste={colar}
          onKeyUp={guardarSelecao}
          onMouseUp={guardarSelecao}
          sx={{
            minHeight,
            px: 1.75,
            py: 1.25,
            outline: 'none',
            lineHeight: 1.55,
            color: disabled ? 'text.disabled' : 'text.primary',
            bgcolor: disabled
              ? 'action.disabledBackground'
              : 'background.paper',
            '& ul': { my: 0.75, pl: 3 },
            '& p': { my: 0.5 },
            ...editorSx,
          }}
        />
      </Paper>
      {helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
      <Dialog
        open={!!conteudoColado}
        onClose={() => setConteudoColado(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Colar conteúdo externo</DialogTitle>
        <Divider />
        <DialogContent>
          <Typography align="left">
            Escolha como deseja inserir o conteúdo copiado:
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'stretch',
            width: '100%',
            px: 3,
            pb: 3,
            gap: 1,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            startIcon={<FormatClear />}
            onClick={() => aplicarColagem(false)}
            sx={{
              width: '100%',
              m: '0 !important',
              bgcolor: 'background.paper',
              whiteSpace: 'nowrap',
            }}
          >
            Colar sem formatação
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<FormatPaint />}
            onClick={() => aplicarColagem(true)}
            sx={{
              width: '100%',
              m: '0 !important',
              bgcolor: 'background.paper',
              whiteSpace: 'nowrap',
            }}
          >
            Colar com formatação
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default FormattedTextEditor
